import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Lock, Plus, Trash2 } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { PersonnelLayout } from '../components/personnel-layout'
import { MobileBottomSlots } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { TableShell } from '../components/ui/table-shell'
import { api, type BlackoutMode, type VacationBlackout } from '../lib/api'
import { notify } from '../lib/notify'

import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/blackouts',
  component: BlackoutsPage,
})

function formatIsoDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function BlackoutsPage() {
  const queryClient = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const isAdmin = meData?.user?.role === 'admin'

  const { data: blackoutsData, isLoading: blackoutsLoading } = useQuery({
    queryKey: ['vacation-blackouts'],
    queryFn: () => api.listVacationBlackouts(),
    enabled: isAdmin,
  })

  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.listTeams(),
    enabled: isAdmin,
  })

  const [editing, setEditing] = useState<VacationBlackout | null>(null)
  const [creating, setCreating] = useState(false)

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  if (!isAdmin) {
    return (
      <PersonnelLayout title="Sperrzeiten">
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
          <p className="text-text-secondary">Sperrzeiten können nur Administratoren verwalten.</p>
        </div>
      </PersonnelLayout>
    )
  }

  const blackouts = blackoutsData?.blackouts ?? []
  const teams = teamsData?.teams ?? []
  const teamName = (id: string | null): string =>
    id ? (teams.find((t) => t.id === id)?.name ?? '—') : 'Alle Teams'

  return (
    <PersonnelLayout
      title="Sperrzeiten"
      description="Zeiträume, in denen Urlaub blockiert oder mit Hinweis versehen wird. Z. B. Betriebsurlaub, Inventur, Jahresabschluss."
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neue Sperrzeit in der Bottom-Bar —
          derselbe Handler wie der Kopf-Button. */}
      <MobileBottomSlots>
        <BarAccentPlus ariaLabel="Neue Sperrzeit" onClick={() => setCreating(true)} />
      </MobileBottomSlots>

      {/* <640 übernimmt die Bottom-Bar — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <div className="flex phone:hidden justify-end mb-4">
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> Neue Sperrzeit
        </Button>
      </div>

      <TableShell className="rounded-2xl border-border-subtle bg-bg-elevated/40">
        {blackoutsLoading ? (
          <div className="px-6 py-12 text-center text-text-tertiary text-sm">Lädt…</div>
        ) : blackouts.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Lock className="mx-auto h-8 w-8 text-text-tertiary mb-3" strokeWidth={1.5} />
            <p className="text-sm text-text-secondary">Noch keine Sperrzeiten angelegt.</p>
            <p className="text-xs text-text-tertiary mt-1">
              Lege Zeiträume an, in denen Urlaub blockiert oder mit Hinweis versehen werden soll.
            </p>
            <Button size="sm" variant="outline" className="mt-4" onClick={() => setCreating(true)}>
              <Plus className="h-3.5 w-3.5" /> Neue Sperrzeit
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle text-text-tertiary text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left py-2.5 px-4 font-medium">Name</th>
                <th className="text-left py-2.5 px-4 font-medium">Zeitraum</th>
                <th className="text-left py-2.5 px-4 font-medium">Modus</th>
                <th className="text-left py-2.5 px-4 font-medium">Team</th>
                <th className="text-left py-2.5 px-4 font-medium">Grund</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {blackouts.map((b) => (
                <tr
                  key={b.id}
                  className="hover:bg-bg-overlay/40 cursor-pointer"
                  onClick={() => setEditing(b)}
                >
                  <td className="py-3 px-4 font-medium">{b.name}</td>
                  <td className="py-3 px-4 tabular-nums text-text-secondary">
                    {formatIsoDate(b.fromDate)} – {formatIsoDate(b.toDate)}
                  </td>
                  <td className="py-3 px-4">
                    {b.blockMode === 'block' ? (
                      <span className="inline-flex items-center rounded-full bg-danger/15 border border-danger/30 px-2 py-0.5 text-2xs uppercase tracking-wider text-danger">
                        Blockiert
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-warning/15 border border-warning/30 px-2 py-0.5 text-2xs uppercase tracking-wider text-warning">
                        Hinweis
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-text-secondary">{teamName(b.teamId)}</td>
                  <td className="py-3 px-4 text-text-tertiary italic truncate max-w-[280px]">
                    {b.reason ?? '—'}
                  </td>
                  <td className="py-3 px-4" />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableShell>

      <BlackoutDialog
        open={creating || !!editing}
        blackout={editing}
        teams={teams}
        onClose={() => {
          setCreating(false)
          setEditing(null)
          queryClient.invalidateQueries({ queryKey: ['vacation-blackouts'] })
        }}
      />
    </PersonnelLayout>
  )
}

function BlackoutDialog({
  open,
  blackout,
  teams,
  onClose,
}: {
  open: boolean
  blackout: VacationBlackout | null
  teams: { id: string; name: string }[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const isEdit = !!blackout
  const [name, setName] = useState('')
  const [fromDate, setFromDate] = useState(todayIso())
  const [toDate, setToDate] = useState(todayIso())
  const [reason, setReason] = useState('')
  const [blockMode, setBlockMode] = useState<BlackoutMode>('warn')
  const [teamId, setTeamId] = useState<string>('')

  useEffect(() => {
    if (!open) return
    if (blackout) {
      setName(blackout.name)
      setFromDate(blackout.fromDate.slice(0, 10))
      setToDate(blackout.toDate.slice(0, 10))
      setReason(blackout.reason ?? '')
      setBlockMode(blackout.blockMode)
      setTeamId(blackout.teamId ?? '')
    } else {
      setName('')
      setFromDate(todayIso())
      setToDate(todayIso())
      setReason('')
      setBlockMode('warn')
      setTeamId('')
    }
  }, [open, blackout])

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        fromDate,
        toDate,
        reason: reason.trim() || null,
        blockMode,
        teamId: teamId || null,
      }
      if (isEdit && blackout) return api.updateVacationBlackout(blackout.id, payload)
      return api.createVacationBlackout(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-blackouts'] })
      notify.success(isEdit ? 'Sperrzeit aktualisiert' : 'Sperrzeit angelegt')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const remove = useMutation({
    mutationFn: () => api.deleteVacationBlackout(blackout?.id ?? ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-blackouts'] })
      notify.success('Sperrzeit gelöscht')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onDelete = async () => {
    if (!blackout) return
    const ok = await confirm({
      title: 'Sperrzeit löschen?',
      description: `${blackout.name} (${formatIsoDate(blackout.fromDate)} – ${formatIsoDate(blackout.toDate)})`,
      variant: 'danger',
      confirmLabel: 'Löschen',
    })
    if (ok) remove.mutate()
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      notify.error('Name darf nicht leer sein')
      return
    }
    if (new Date(toDate) < new Date(fromDate)) {
      notify.error('Bis-Datum muss nach Von-Datum liegen')
      return
    }
    save.mutate()
  }

  const isPending = save.isPending || remove.isPending

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={isEdit ? 'Sperrzeit bearbeiten' : 'Neue Sperrzeit'}
      footer={
        <>
          {isEdit && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              type="button"
              disabled={isPending}
              className="mr-auto text-danger hover:bg-danger/10 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" /> Löschen
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="bo-name">Name</Label>
          <Input
            id="bo-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Betriebsurlaub Weihnachten"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="bo-from">Von</Label>
            <Input
              id="bo-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bo-to">Bis</Label>
            <Input
              id="bo-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="bo-mode">Modus</Label>
          <Select
            id="bo-mode"
            value={blockMode}
            onChange={(e) => setBlockMode(e.target.value as BlackoutMode)}
          >
            <option value="warn">Hinweis (Urlaub trotzdem möglich)</option>
            <option value="block">Blockiert (Urlaubsantrag wird abgelehnt)</option>
          </Select>
          <p className="text-2xs text-text-tertiary">
            Admins umgehen das Blockieren und können trotzdem Urlaube anlegen.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="bo-team">Gilt für</Label>
          <Select id="bo-team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">Alle Mitarbeiter</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                Team: {t.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="bo-reason">Beschreibung (optional)</Label>
          <Input
            id="bo-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="z. B. Inventur, Jahresabschluss"
          />
        </div>
      </form>
    </SidePanel>
  )
}
